import { listUsers } from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import type { UserListItem } from '@jq4race/shared';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@jq4race/ui';
import { useCallback, useEffect, useRef, useState } from 'react';

import { ASSIGNABLE_ROLES } from '../constants';

interface AssignUserModalProps {
  open: boolean;
  alreadyAssignedIds: Set<string>;
  submitting: boolean;
  onAssign: (userId: string) => void;
  onClose: () => void;
}

export function AssignUserModal({
  open,
  alreadyAssignedIds,
  submitting,
  onAssign,
  onClose,
}: AssignUserModalProps) {
  const { t } = useTranslation('location-assignments');
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [candidates, setCandidates] = useState<UserListItem[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const seqRef = useRef(0);

  // Reset state when modal opens or closes.
  useEffect(() => {
    if (open) {
      setSelectedUserId('');
      setSearchQuery('');
      setDebouncedQuery('');
      setCandidates([]);
    }
  }, [open]);

  // Debounce the search input by 300 ms.
  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);
    return () => window.clearTimeout(id);
  }, [searchQuery]);

  // Fetch users whenever the debounced query changes and the modal is open.
  const fetchUsers = useCallback(
    async (q: string) => {
      if (!open) return;
      seqRef.current += 1;
      const seq = seqRef.current;
      setLoadingUsers(true);
      try {
        const res = await listUsers({
          q: q || undefined,
          page: 1,
          limit: 50,
          roles: ASSIGNABLE_ROLES,
        });
        if (seq !== seqRef.current) return;
        setCandidates(res.users);
      } catch {
        if (seq !== seqRef.current) return;
        setCandidates([]);
      } finally {
        if (seq === seqRef.current) setLoadingUsers(false);
      }
    },
    [open],
  );

  useEffect(() => {
    void fetchUsers(debouncedQuery);
  }, [debouncedQuery, fetchUsers]);

  // Only show active users who are not already assigned (role filter is applied server-side).
  const eligibleUsers = candidates.filter(
    (u) => u.email && u.active && !alreadyAssignedIds.has(u.id),
  );

  function handleConfirm() {
    if (!selectedUserId) return;
    onAssign(selectedUserId);
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? onClose() : undefined)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('modal.title')}</DialogTitle>
          <DialogDescription>{t('modal.description')}</DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-3">
          <input
            type="text"
            placeholder={t('modal.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setSelectedUserId('');
            }}
            className="w-full rounded-md border border-jq-sand/70 bg-jq-surface-1 px-3 py-2 text-[13px] text-jq-charcoal placeholder:text-jq-graphite/60 focus:border-jq-navy/50 focus:outline-none focus:ring-1 focus:ring-jq-navy/30"
          />

          {loadingUsers ? (
            <p className="py-4 text-center text-[13px] text-jq-graphite">
              {t('common:states.loading')}
            </p>
          ) : eligibleUsers.length === 0 ? (
            <p className="rounded-sm border border-jq-sand/70 bg-jq-surface-2/50 px-4 py-4 text-center text-[13px] text-jq-graphite">
              {t('modal.noEligibleUsers')}
            </p>
          ) : (
            <div className="max-h-64 space-y-1.5 overflow-y-auto pr-1">
              {eligibleUsers.map((user) => {
                const isSelected = user.id === selectedUserId;
                return (
                  <button
                    key={user.id}
                    type="button"
                    onClick={() => setSelectedUserId(user.id)}
                    aria-pressed={isSelected}
                    className={[
                      'flex w-full items-center gap-3 rounded-md border px-3 py-2.5 text-left text-[13px] transition',
                      isSelected
                        ? 'border-jq-navy bg-jq-navy/5 font-medium text-jq-navy'
                        : 'border-jq-sand/70 text-jq-charcoal hover:border-jq-navy/40 hover:bg-jq-surface-1',
                    ].join(' ')}
                  >
                    <span
                      className={[
                        'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border-2',
                        isSelected ? 'border-jq-navy bg-jq-navy' : 'border-jq-stone',
                      ].join(' ')}
                    >
                      {isSelected && (
                        <span className="h-1 w-1 rounded-full bg-jq-paper" aria-hidden />
                      )}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{user.email}</span>
                      <span className="block text-[11px] text-jq-graphite">
                        {t(`roles.${user.role}`)}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={submitting}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleConfirm}
            disabled={submitting || !selectedUserId}
          >
            {submitting ? t('modal.assigning') : t('modal.assign')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
